import React, { useState, useEffect } from 'react';
import { Card, Form, Button, InputGroup, FormControl } from 'react-bootstrap';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { getTrackingUrl } from '../../services/config';
import { getBackendUrl } from '../../services/config/backendUrl';
import { getAuthHeaders } from '../../services/auth/authHeaders';

const Documentation = () => {
  const [showToken, setShowToken] = useState(false);
  const [authToken, setAuthToken] = useState('Loading...');
  const [trackingUrl, setTrackingUrl] = useState('Loading...');
  const backendUrl = getBackendUrl();

  const curlExample = `curl -X POST "${backendUrl}/api/subscribers/add-tag" \\
  -H "Authorization: Bearer ${authToken}" \\
  -H "Content-Type: application/json" \\
  -d '{
    "email": "test@example.com",
    "tag": "newsletter",
    "source": "website",
    "name": "John Doe",
    "phone": "+1234567890",
    "countryCode": "US",
    "sendWelcomeEmail": true,
    "topicIds": "1,2,3",
    "skipDebounce": false
  }'`;

  useEffect(() => {
    getTrackingUrl().then(setTrackingUrl);
    fetch(`${backendUrl}/api/config/auth-token`, {
      headers: getAuthHeaders(),
    })
      .then(res => res.json())
      .then(data => setAuthToken(data.authToken || 'Not configured'))
      .catch(() => setAuthToken('Error loading token'));
  }, [backendUrl]);

  const toggleTokenVisibility = () => {
    setShowToken(!showToken);
  };

  return (
    <>
      <Card className="m-3">
        <Card.Header as="h5" style={{ backgroundColor: '#d1c4e9' }}>API Documentation</Card.Header>
        <Card.Body>
          <Card.Title><code>POST /api/subscribers/add-tag</code></Card.Title>
          <Card.Text>
            This endpoint allows you to add a specific tag to a subscriber identified by their email address.
            If the subscriber doesn't exist, they will be created automatically with the specified tag.
            If the tag does not exist in the system, it will be created automatically.
          </Card.Text>

          <h6 className="mb-2 text-primary">Authentication:</h6>
          <Card.Text>
            This endpoint requires authentication using a Bearer token in the <code>Authorization</code> header.
            The token is a pre-defined secret key configured on the backend server.
          </Card.Text>

          <Form.Group className="mb-3">
            <Form.Label>Authentication Token:</Form.Label>
            <InputGroup>
              <FormControl
                type={showToken ? 'text' : 'password'}
                value={authToken}
                readOnly
                aria-label="Authentication Token"
              />
              <Button variant="outline-secondary" onClick={toggleTokenVisibility}>
                {showToken ? <VisibilityOffIcon /> : <VisibilityIcon />}
              </Button>
            </InputGroup>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Backend Base URL:</Form.Label>
            <FormControl
              type="text"
              value={backendUrl}
              readOnly
              aria-label="Backend Base URL"
            />
          </Form.Group>

          <h6 className="mb-2 text-primary">Request Body:</h6>
          <Card.Text>
            The request body should be a JSON object with the following properties:
            <ul>
              <li><code>email</code> (string, required): The email address of the subscriber.</li>
              <li><code>tag</code> (string, required): The tag to be added to the subscriber.</li>
              <li><code>source</code> (string, optional): The source for new subscribers (defaults to "api").</li>
              <li><code>name</code> (string, optional): The subscriber's name.</li>
              <li><code>phone</code> (string, optional): The subscriber's phone number.</li>
              <li><code>ipAddress</code> (string, optional): The subscriber's IP address.</li>
              <li><code>countryCode</code> (string, optional): The subscriber's country code.</li>
              <li><code>sendWelcomeEmail</code> (boolean, optional): Send welcome email to new subscribers (defaults to false).</li>
              <li><code>topicIds</code> (string, optional): Comma-separated topic IDs (e.g., "1,2,3"). If not provided, subscriber is assigned to all existing topics.</li>
              <li><code>skipDebounce</code> (boolean, optional): If true, the subscriber will skip debounce validation and won't be checked against the debounce service (defaults to false).</li>
            </ul>
          </Card.Text>

          <h6 className="mb-2 text-primary">Example Request:</h6>
          <pre className="bg-light p-3 rounded">
            <code>{`{
  "email": "test@example.com",
  "tag": "newsletter",
  "source": "website",
  "name": "John Doe",
  "phone": "+1234567890",
  "countryCode": "US",
  "sendWelcomeEmail": true,
  "topicIds": "1,2,3",
  "skipDebounce": false
}`}</code>
          </pre>

          <h6 className="mb-2 text-primary">Copy-Pasteable cURL:</h6>
          <div className="position-relative">
            <Button
              variant="outline-primary"
              size="sm"
              className="position-absolute top-0 end-0 m-2"
              onClick={() => navigator.clipboard.writeText(curlExample)}
            >
              Copy
            </Button>
            <pre className="bg-light p-3 rounded pt-5">
              <code>{curlExample}</code>
            </pre>
          </div>

          <h6 className="mb-2 text-primary">Success Response (Status 200 OK):</h6>
          <pre className="bg-light p-3 rounded">
            <code>{`// For existing subscriber:
{
  "message": "Subscriber 'test@example.com' tagged with 'newsletter' successfully."
}

// For new subscriber:
{
  "message": "Subscriber 'test@example.com' created and tagged with 'newsletter' successfully."
}`}</code>
          </pre>

          <h6 className="mb-2 text-primary">Error Responses:</h6>
          <ul>
            <li><code>400 Bad Request</code>: If 'email' or 'tag' are missing from the request body.</li>
            <li><code>403 Forbidden</code>: If the authentication token is missing or invalid.</li>
            <li><code>500 Internal Server Error</code>: For other server-side errors.</li>
          </ul>
          
          <h6 className="mb-2 text-success">Features:</h6>
          <Card.Text className="text-success">
            <strong>Auto-Create Subscribers:</strong> If the email doesn't exist in the database, 
            a new subscriber will be automatically created with optIn=true, 
            and the specified source (defaults to "api").
            <br /><br />
            <strong>Welcome Emails:</strong> Set <code>sendWelcomeEmail: true</code> to automatically send 
            welcome emails to new subscribers. Requires a template named "welcome" to be created first.
          </Card.Text>
        </Card.Body>
      </Card>

      <Card className="m-3">
        <Card.Header as="h5" style={{ backgroundColor: '#d1c4e9' }}>Bulk API Documentation</Card.Header>
        <Card.Body>
          <Card.Title><code>POST /api/subscribers/bulk-add-tag</code></Card.Title>
          <Card.Text>
            This endpoint allows you to add tags to multiple subscribers in a single request.
            Subscribers will be created automatically if they don't exist.
          </Card.Text>

          <h6 className="mb-2 text-primary">Authentication:</h6>
          <Card.Text>
            Same Bearer token authentication as the single subscriber endpoint.
          </Card.Text>

          <h6 className="mb-2 text-primary">Request Body:</h6>
          <Card.Text>
            <ul>
              <li><code>subscribers</code> (array, required): Array of subscriber objects with email, tag, and optional properties.</li>
              <li><code>source</code> (string, optional): Source for new subscribers (defaults to "api").</li>
              <li><code>sendWelcomeEmail</code> (boolean, optional): Send welcome email to new subscribers (defaults to false).</li>
              <li><code>skipDebounce</code> (boolean, optional): Default debounce behavior for all subscribers (defaults to false). Can be overridden per-subscriber.</li>
            </ul>
            Each subscriber object can contain:
            <ul>
              <li><code>email</code> (string, required): The email address.</li>
              <li><code>tag</code> (string, required): The tag to be added.</li>
              <li><code>name</code> (string, optional): The subscriber's name.</li>
              <li><code>phone</code> (string, optional): The subscriber's phone number.</li>
              <li><code>ipAddress</code> (string, optional): The subscriber's IP address.</li>
              <li><code>countryCode</code> (string, optional): The subscriber's country code.</li>
              <li><code>topicIds</code> (string, optional): Comma-separated topic IDs (e.g., "1,2,3"). If not provided, subscriber is assigned to all existing topics.</li>
              <li><code>skipDebounce</code> (boolean, optional): If true, the subscriber will skip debounce validation and won't be checked against the debounce service (defaults to false).</li>
            </ul>
          </Card.Text>

          <h6 className="mb-2 text-primary">Example Request:</h6>
          <pre className="bg-light p-3 rounded">
            <code>{`{
  "subscribers": [
    {
      "email": "user1@example.com", 
      "tag": "newsletter",
      "name": "Alice Smith",
      "countryCode": "CA",
      "topicIds": "1,2,3"
    },
    {
      "email": "user2@example.com", 
      "tag": "premium",
      "phone": "+44123456789",
      "topicIds": "1"
    },
    {
      "email": "user3@example.com", 
      "tag": "newsletter"
    }
  ],
  "source": "website",
  "sendWelcomeEmail": true
}`}</code>
          </pre>

          <h6 className="mb-2 text-primary">Success Response (Status 200 OK):</h6>
          <pre className="bg-light p-3 rounded">
            <code>{`{
  "message": "Bulk operation completed: 2 created, 1 updated",
  "created": 2,
  "updated": 1,
  "errors": []
}`}</code>
          </pre>

          <h6 className="mb-2 text-primary">Error Responses:</h6>
          <ul>
            <li><code>400 Bad Request</code>: If subscribers array is missing or empty.</li>
            <li><code>403 Forbidden</code>: If authentication token is invalid.</li>
            <li><code>500 Internal Server Error</code>: For server-side errors.</li>
          </ul>
        </Card.Body>
      </Card>

      <Card className="m-3">
        <Card.Header as="h5" style={{ backgroundColor: '#d1c4e9' }}>Subscriber Suppression API</Card.Header>
        <Card.Body>
          <Card.Title><code>POST /api/subscribers/suppress</code></Card.Title>
          <Card.Text>
            This endpoint opts out subscribers who mark email as spam/abuse, bounce, or unsubscribe.
            Supports both global opt-out (all topics) and topic-scoped opt-out (specific topics only).
            This is a webhook receiver endpoint for machine-to-machine authentication.
          </Card.Text>

          <h6 className="mb-2 text-primary">Authentication:</h6>
          <Card.Text>
            Bearer token authentication using the AUTH_TOKEN from KV config.
            Use header: <code>Authorization: Bearer &lt;AUTH_TOKEN&gt;</code>
          </Card.Text>

          <h6 className="mb-2 text-primary">Request Body:</h6>
          <Card.Text>
            <ul>
              <li><code>events</code> (array, required): Array of suppression event objects.</li>
            </ul>
            Each event object contains:
            <ul>
              <li><code>email</code> (string, required): The email address to suppress.</li>
              <li><code>reason</code> (string, required): Reason for suppression (e.g., "complaint", "bounce", "fbl", "manual_api", "system_optout").</li>
              <li><code>topicIds</code> (number[], optional): Specific topic IDs to unsubscribe from. If omitted, performs global opt-out.</li>
              <li><code>timestamp</code> (string, optional): ISO 8601 timestamp of the event.</li>
            </ul>
          </Card.Text>

          <h6 className="mb-2 text-primary">Topic-Scoping Behavior:</h6>
          <Card.Text>
            <ul>
              <li><strong>Global opt-out</strong> (topicIds omitted or empty): Sets subscriber's <code>optIn = 0</code> and unsubscribes from ALL topics.</li>
              <li><strong>Topic-scoped opt-out</strong> (topicIds provided, e.g., [3,7]): Only unsubscribes from specified topics. Subscriber's <code>optIn</code> stays <code>1</code> (can receive other topics).</li>
            </ul>
          </Card.Text>

          <h6 className="mb-2 text-primary">Example Request (Global Opt-Out):</h6>
          <pre className="bg-light p-3 rounded">
            <code>{`{
  "events": [
    { "email": "user1@example.com", "reason": "complaint" },
    { "email": "user2@example.com", "reason": "bounce" }
  ]
}`}</code>
          </pre>

          <h6 className="mb-2 text-primary">Example Request (Topic-Scoped Opt-Out):</h6>
          <pre className="bg-light p-3 rounded">
            <code>{`{
  "events": [
    { "email": "user1@example.com", "reason": "fbl", "topicIds": [3, 7] }
  ]
}`}</code>
          </pre>

          <h6 className="mb-2 text-primary">Success Response (Status 200 OK):</h6>
          <pre className="bg-light p-3 rounded">
            <code>{`{
  "success": true,
  "processed": 2,
  "notFound": 0,
  "skipped": 0
}`}</code>
          </pre>

          <h6 className="mb-2 text-primary">Error Responses:</h6>
          <ul>
            <li><code>400 Bad Request</code>: If events array is missing or empty.</li>
            <li><code>403 Forbidden</code>: If authentication token is invalid or missing.</li>
            <li><code>500 Internal Server Error</code>: For server-side errors.</li>
          </ul>
        </Card.Body>
      </Card>

      <Card className="m-3">
        <Card.Header as="h5" style={{ backgroundColor: '#d1c4e9' }}>CSV Upload API Documentation</Card.Header>
        <Card.Body>
          <Card.Title><code>POST /api/subscribers/upload-csv</code></Card.Title>
          <Card.Text>
            This endpoint allows you to bulk import subscribers from a CSV file. The CSV file is parsed,
            subscribers are created, and tags are automatically created and assigned.
          </Card.Text>

          <h6 className="mb-2 text-primary">Authentication:</h6>
          <Card.Text>
            Same Bearer token authentication as the subscriber tagging endpoints.
          </Card.Text>

          <h6 className="mb-2 text-primary">Request (multipart/form-data):</h6>
          <Card.Text>
            <ul>
              <li><code>file</code> (File, required): CSV file with the following columns:</li>
            </ul>
            <table className="table table-bordered table-sm">
              <thead>
                <tr><th>Column</th><th>Required</th><th>Description</th></tr>
              </thead>
              <tbody>
                <tr><td><code>email</code></td><td>Yes</td><td>Email address of the subscriber</td></tr>
                <tr><td><code>source</code></td><td>Yes</td><td>Source identifier (e.g., "website", "manual")</td></tr>
                <tr><td><code>name</code></td><td>No</td><td>Subscriber's name</td></tr>
                <tr><td><code>phone</code></td><td>No</td><td>Subscriber's phone number</td></tr>
                <tr><td><code>optIn</code></td><td>No</td><td>Opt-in status (true/false/1/0, defaults to true)</td></tr>
                <tr><td><code>ipAddress</code></td><td>No</td><td>Subscriber's IP address</td></tr>
                <tr><td><code>countryCode</code></td><td>No</td><td>Subscriber's country code</td></tr>
                <tr><td><code>tags</code></td><td>No</td><td>Dash-separated tags (e.g., "newsletter-premium-vip")</td></tr>
                <tr><td><code>entryDate</code></td><td>No</td><td>Entry date (ISO 8601 format)</td></tr>
                <tr><td><code>lastOpened</code></td><td>No</td><td>Last opened date (ISO 8601 format)</td></tr>
              </tbody>
            </table>
            <ul>
              <li><code>skipDebounce</code> (boolean, optional): If true, all imported subscribers will skip debounce validation (defaults to false).</li>
            </ul>
          </Card.Text>

          <h6 className="mb-2 text-primary">Example CSV:</h6>
          <pre className="bg-light p-3 rounded">
            <code>{`email,source,name,phone,optIn,tags
john@example.com,website,John Doe,+1234567890,true,newsletter-premium
jane@example.com,manual,Jane Smith,,,vip`}</code>
          </pre>

          <h6 className="mb-2 text-primary">Success Response (Status 200 OK):</h6>
          <pre className="bg-light p-3 rounded">
            <code>{`{
  "message": "Successfully imported 100 subscribers, created 3 new tags, and established 150 tag relationships",
  "imported": 100,
  "tagsCreated": 3,
  "tagRelationships": 150,
  "errors": [],
  "totalErrors": 0,
  "skipped": 0
}`}</code>
          </pre>

          <h6 className="mb-2 text-primary">Error Responses:</h6>
          <ul>
            <li><code>400 Bad Request</code>: If no file uploaded, invalid CSV format, or missing required columns.</li>
            <li><code>403 Forbidden</code>: If authentication token is invalid.</li>
            <li><code>500 Internal Server Error</code>: For server-side errors.</li>
          </ul>

          <h6 className="mb-2 text-success">Features:</h6>
          <Card.Text className="text-success">
            <strong>Auto-Create Tags:</strong> Tags specified in the CSV are automatically created if they don't exist.
            <br /><br />
            <strong>Tag Relationship Management:</strong> Subscriber-tag relationships are efficiently managed via the junction table.
          </Card.Text>
        </Card.Body>
      </Card>

      <Card className="m-3">
        <Card.Header as="h5" style={{ backgroundColor: '#d1c4e9' }}>Email Tracking API Documentation</Card.Header>
        <Card.Body>
          <Card.Title><code>GET /track</code></Card.Title>
          <Card.Text>
            This endpoint handles email tracking for opens and clicks. It's automatically called when 
            recipients open emails or click links in campaigns.
          </Card.Text>

          <h6 className="mb-2 text-primary">Default Tracking URL:</h6>
          <pre className="bg-light p-3 rounded">
            <code>{trackingUrl}</code>
          </pre>

          <h6 className="mb-2 text-primary">Query Parameters:</h6>
          <Card.Text>
            <ul>
              <li><code>data</code> (string, required): Base64 encoded JSON containing tracking information.</li>
            </ul>
          </Card.Text>

          <h6 className="mb-2 text-primary">Tracking Data Structure:</h6>
          <pre className="bg-light p-3 rounded">
            <code>{`// For email opens:
{
  "broadcast_name": "campaign-tag",
  "email": "user@example.com",
  "status": "read"
}

// For link clicks:
{
  "broadcast_name": "campaign-tag",
  "email": "user@example.com",
  "status": "click",
  "url": "https://original-link.com"
}`}</code>
          </pre>

          <h6 className="mb-2 text-primary">How It Works:</h6>
          <Card.Text>
            <ul>
              <li><strong>Email Opens:</strong> 1x1 pixel image automatically added to emails</li>
              <li><strong>Link Clicks:</strong> All links in emails are automatically wrapped with tracking</li>
              <li><strong>Data Storage:</strong> Events stored in DynamoDB for analytics</li>
              <li><strong>Subscriber Tagging:</strong> Automatic tagging based on engagement (opens, clicks)</li>
            </ul>
          </Card.Text>

          <h6 className="mb-2 text-primary">Custom Tracking URLs:</h6>
          <Card.Text>
            You can specify a custom tracking URL when creating campaigns. This allows you to:
            <ul>
              <li>Use your own tracking domain</li>
              <li>Route tracking through different services</li>
              <li>Implement custom tracking logic</li>
            </ul>
          </Card.Text>

          <h6 className="mb-2 text-success">Automatic Integration:</h6>
          <Card.Text className="text-success">
            <strong>No Manual Setup Required:</strong> Tracking is automatically integrated into all campaign emails. 
            The system handles pixel insertion and link wrapping transparently.
          </Card.Text>
        </Card.Body>
      </Card>

      <Card className="m-3">
        <Card.Header as="h5" style={{ backgroundColor: '#d1c4e9' }}>Bulk Email Tracking API Documentation</Card.Header>
        <Card.Body>
          <Card.Title><code>POST /track/bulk</code></Card.Title>
          <Card.Text>
            This endpoint handles bulk email tracking events for high-volume email processing. 
            It accepts multiple tracking events in a single request for efficient processing.
          </Card.Text>

          <h6 className="mb-2 text-primary">Request Body:</h6>
          <Card.Text>
            Array of tracking event objects, each containing:
            <ul>
              <li><code>email</code> (string, required): Recipient email address</li>
              <li><code>broadcast_name</code> (string, required): Campaign identifier</li>
              <li><code>status</code> (string, required): Event type (sent, delivery, bounce, etc.)</li>
              <li><code>reason</code> (string, optional): Additional event details</li>
              <li><code>res_code</code> (string, optional): Response code from mail server. Values matching the configured auto-opt-out codes will automatically unsubscribe the subscriber.</li>
              <li><code>sender_ip</code> (string, optional): IP address of the sending server. Defaults to "0.0.0.0".</li>
              <li><code>created</code> (number, optional): Unix timestamp of when the event occurred. Defaults to current server time.</li>
            </ul>
          </Card.Text>

          <h6 className="mb-2 text-primary">Example Request:</h6>
          <pre className="bg-light p-3 rounded">
            <code>{`[
  {
    "email": "user1@example.com",
    "broadcast_name": "campaign-tag",
    "status": "sent",
    "reason": "",
    "sender_ip": "10.0.0.1",
    "res_code": "250",
    "created": 1743435600
  },
  {
    "email": "user2@example.com",
    "broadcast_name": "campaign-tag",
    "status": "delivery",
    "reason": "",
    "sender_ip": "10.0.0.1",
    "res_code": "250",
    "created": 1743435601
  },
  {
    "email": "user3@example.com",
    "broadcast_name": "campaign-tag",
    "status": "bounce",
    "reason": "Invalid email address",
    "sender_ip": "10.0.0.2",
    "res_code": "550",
    "created": 1743435602
  }
]`}</code>
          </pre>

          <h6 className="mb-2 text-primary">Success Response (Status 200 OK):</h6>
          <pre className="bg-light p-3 rounded">
            <code>Bulk tracked</code>
          </pre>

          <h6 className="mb-2 text-primary">Features:</h6>
          <Card.Text>
            <ul>
              <li><strong>Batch Processing:</strong> Processes up to 25 events per DynamoDB batch</li>
              <li><strong>Automatic Tagging:</strong> Updates subscriber tags based on email events</li>
              <li><strong>Error Handling:</strong> Skips malformed events, continues processing valid ones</li>
              <li><strong>Parallel Processing:</strong> DynamoDB writes and subscriber updates run concurrently</li>
            </ul>
          </Card.Text>

          <h6 className="mb-2 text-primary">Status Mapping:</h6>
          <Card.Text>
            Email events are automatically mapped to single-character tags:
            <ul>
              <li><strong>sent/reception/transistfailure</strong> → 's' (sent)</li>
              <li><strong>delivery/delivered</strong> → 'd' (delivered)</li>
              <li><strong>read</strong> → 'o' (opened)</li>
              <li><strong>bounce/bounced/failed/expiration</strong> → 'b' (bounced)</li>
              <li><strong>click</strong> → 'c' (clicked)</li>
            </ul>
          </Card.Text>

          <h6 className="mb-2 text-warning">Usage Notes:</h6>
          <Card.Text className="text-warning">
            <strong>High Volume Processing:</strong> This endpoint is designed for bulk email service integrations 
            and can handle thousands of events per request efficiently.
          </Card.Text>
        </Card.Body>
      </Card>
    </>
  );
};

export default Documentation;
